/**
 * AI CONTEXT
 * Purpose: pure helpers for the profile's tiled Artwork tab — turning one
 *   student's `artwork` map into semester sections (with per-class buckets),
 *   newest first, plus the tab's filters, its hidden-visibility step, and
 *   its write helpers (rotate, add to portfolio, hide/unhide).
 * Key deps: semesterHelpers + classOptions labels, so this stays independent
 *   of the artwork wall work on the JARVIS-125 branch.
 * Referenced by: StudentArtworkTilesSection, StudentProfileTabs.
 */
import dayjs from "dayjs"
import {
  filter,
  groupBy,
  isEmpty,
  map,
  partition,
  sortBy,
  values,
} from "lodash"

import { CLASS_GROUP_LABELS, CLASS_GROUPS } from "#constants/classOptions"
import {
  ALL_SKILL_LEVEL_LABELS,
  ALL_SKILL_LEVELS,
  CORE_SKILL_LEVELS,
  type CoreSkillLevel,
  getStudentSkill,
} from "#constants/skillLevelConstants"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  type StudentArtwork,
  type StudentConstructor,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { STANDARD_DATE_FORMAT, STUDENT_DOB_FORMAT } from "#utils/dateHelpers"
import { formatSemester, getCurrentSemester } from "#utils/semesterHelpers"

/** The pseudo class id the mobile upload path stamps on artwork. */
export const MOBILE_UPLOAD_CLASS_ID = "mobile-upload"
const MOBILE_UPLOAD_CLASS_LABEL = "Mobile Uploads"
const UNKNOWN_CLASS_LABEL = "Unknown Class"

export interface ArtworkTile {
  id: string
  artwork: StudentArtwork
  /** `artwork.semester`, else the semester `timeCreated` falls in. */
  semester: string
  /**
   * Core level in force for `semester`, read from `skillHistory` only — never
   * the top-level `coreSkillLevel`, which is "now" rather than history.
   * Empty when nothing is on record at or before that semester.
   */
  coreLevel: CoreSkillLevel | ""
  /** Whole years old when the piece was made; null without a usable dob. */
  ageAtCreation: number | null
  classId: string
  /** Class display name; "Mobile Uploads" / "Unknown Class" fallbacks. */
  className: string
  /** The class's group, when the class doc is loaded and has one. */
  classGroup: CLASS_GROUPS | null
  /**
   * Competitions the piece was entered in, as opaque ids ("FamilyFA26").
   * Display names live in the mobile BFF's COMPETITIONS constant, never in
   * Jarvis, so the UI shows these ids as-is. Legacy single `competitionId`
   * is folded in.
   */
  competitionIds: readonly string[]
  /**
   * True when the piece is not yet in the portfolio. Mirrors the old artwork
   * section: `inPortfolio` wins when set, else pieces from PORTFOLIO (AP)
   * classes count as already in.
   */
  showAddToPortfolioCta: boolean
  /**
   * Display label: created date · dob-formatted student name · class name ·
   * project name (when the piece belongs to a portfolio project). The
   * filename is deliberately absent — it is scanner metadata, not a title.
   */
  caption: string
  /**
   * True when the piece is hidden (nondestructive delete). Every piece
   * still becomes a tile; visibility is decided later by
   * `selectVisibleArtworkTiles`, so the "Show hidden" toggle can count and
   * reveal these.
   */
  isHidden: boolean
}

/** Tiles of one class within one semester, labeled with the class name. */
export interface ArtworkTileClassBucket {
  key: string
  label: string
  tiles: ArtworkTile[]
}

export interface ArtworkTileSection {
  /** The semester key, e.g. `2026 B`. Stable, used as the React key. */
  key: string
  title: string
  subtitle: string
  tiles: ArtworkTile[]
  /** `tiles` split by class, newest class first, for labeled sub-groups. */
  classBuckets: ArtworkTileClassBucket[]
}

/**
 * The semester a piece belongs to. Mirrors how the old artwork section derived
 * it: the stored semester wins, and only artwork without one is dated by when
 * it was created.
 */
export const getArtworkSemester = (
  artwork: Pick<StudentArtwork, "semester" | "timeCreated">
): string =>
  artwork.semester ||
  getCurrentSemester(dayjs(artwork.timeCreated).format(STANDARD_DATE_FORMAT))

const isCoreSkillLevel = (value: string): value is CoreSkillLevel =>
  value in CORE_SKILL_LEVELS

/** The core level in force for `semester`, walking `skillHistory` backwards. */
export const getCoreLevelForSemester = (
  skillHistory: StudentConstructor["skillHistory"],
  semester: string
): CoreSkillLevel | "" => {
  const level = getStudentSkill(skillHistory, StudentSkillType.core, semester)
  return isCoreSkillLevel(level) ? level : ""
}

/** Whole years between `dob` and `timeCreated`; null unless both are usable. */
export const getAgeAt = (
  dob: string | undefined,
  timeCreated: number | undefined
): number | null => {
  if (!dob || !timeCreated) return null
  const born = dayjs(dob)
  const made = dayjs(timeCreated)
  if (!born.isValid() || !made.isValid()) return null
  const years = made.diff(born, "year")
  return Number.isFinite(years) && years >= 0 ? years : null
}

/**
 * Competition ids for one piece: the array field, with the legacy single
 * `competitionId` folded in (pre-array shape, never backfilled — see the
 * StudentArtwork type). Deduped, order preserved.
 */
export const getCompetitionIds = (
  artwork: Pick<StudentArtwork, "competitionIds" | "competitionId">
): readonly string[] => {
  const ids = [
    ...(artwork.competitionIds ?? []),
    ...(artwork.competitionId ? [artwork.competitionId] : []),
  ]
  return [...new Set(ids)]
}

/**
 * The image every surface should show for a piece: the mobile crop when the
 * user made one, else the original scan. `url` is never replaced, so anything
 * that renders (or copies) a piece has to resolve the pair itself.
 */
export const getArtworkDisplayUrl = (
  artwork: Pick<StudentArtwork, "cropUrl" | "url">
): string => artwork.cropUrl ?? artwork.url

const getClassLabel = (
  classId: string,
  classesById: Record<Uuid, ClassConstructor> | undefined
): string => {
  if (classId === MOBILE_UPLOAD_CLASS_ID) return MOBILE_UPLOAD_CLASS_LABEL
  return classesById?.[classId]?.name ?? UNKNOWN_CLASS_LABEL
}

/** `MMYY name` when the dob is usable, matching Student.getNameAndDob. */
const getNameWithDob = (
  name: string | undefined,
  dob: string | undefined
): string => {
  if (!name) return ""
  const born = dob ? dayjs(dob) : null
  return born?.isValid() ? `${born.format(STUDENT_DOB_FORMAT)} ${name}` : name
}

/**
 * Every piece as a tile, newest first. `classesById` enriches tiles with the
 * class name and group; while class docs are still loading the fallback
 * labels render and correct themselves when the subscription fills.
 * `projectTitlesById` (from the student's portfolio doc) names the project
 * in the caption when the piece belongs to one.
 */
export const buildArtworkTiles = (
  student:
    | Pick<StudentConstructor, "artwork" | "skillHistory" | "dob" | "name">
    | undefined,
  classesById: Record<Uuid, ClassConstructor> | undefined,
  projectTitlesById?: Record<string, string>
): ArtworkTile[] => {
  const nameWithDob = getNameWithDob(student?.name, student?.dob)
  const tiles = map(student?.artwork ?? {}, (artwork) => {
    const semester = getArtworkSemester(artwork)
    const classGroup = classesById?.[artwork.classId]?.group ?? null
    const inPortfolio =
      artwork.inPortfolio ?? classGroup === CLASS_GROUPS.PORTFOLIO
    const className = getClassLabel(artwork.classId, classesById)
    const projectName =
      (artwork.projectId && projectTitlesById?.[artwork.projectId]) || ""
    const caption = [
      artwork.timeCreated
        ? dayjs(artwork.timeCreated).format(STANDARD_DATE_FORMAT)
        : "",
      nameWithDob,
      className,
      projectName,
    ]
      .filter(Boolean)
      .join(" · ")
    return {
      id: artwork.id,
      artwork,
      semester,
      coreLevel: getCoreLevelForSemester(student?.skillHistory, semester),
      ageAtCreation: getAgeAt(student?.dob, artwork.timeCreated),
      classId: artwork.classId,
      className,
      classGroup,
      competitionIds: getCompetitionIds(artwork),
      showAddToPortfolioCta: !inPortfolio,
      caption,
      isHidden: artwork.isHidden === true,
    }
  })
  return sortBy(tiles, (tile) => -(tile.artwork.timeCreated ?? 0))
}

/** Filter value meaning "artwork whose semester has no core level on record". */
export const NO_LEVEL_FILTER_VALUE = "__none__"

/** Filter value for artwork whose class has no group (e.g. mobile uploads). */
export const NO_CLASS_GROUP_FILTER_VALUE = "__none__"

export interface ArtworkTileFilters {
  /** Inclusive epoch-ms bounds on `timeCreated`; null = unbounded. */
  dateRange: [number, number] | null
  /** Core levels (or NO_LEVEL_FILTER_VALUE); empty = all. */
  levels: readonly string[]
  /** CLASS_GROUPS values (or NO_CLASS_GROUP_FILTER_VALUE); empty = all. */
  classGroups: readonly string[]
  ageMin: number | null
  ageMax: number | null
}

export const EMPTY_ARTWORK_TILE_FILTERS: ArtworkTileFilters = {
  dateRange: null,
  levels: [],
  classGroups: [],
  ageMin: null,
  ageMax: null,
}

const matchesDateRange = (
  tile: ArtworkTile,
  range: [number, number] | null
): boolean => {
  if (!range) return true
  const created = tile.artwork.timeCreated
  return created >= range[0] && created <= range[1]
}

const matchesLevel = (
  tile: ArtworkTile,
  levels: readonly string[]
): boolean => {
  if (levels.length === 0) return true
  return tile.coreLevel === ""
    ? levels.includes(NO_LEVEL_FILTER_VALUE)
    : levels.includes(tile.coreLevel)
}

const matchesClassGroup = (
  tile: ArtworkTile,
  classGroups: readonly string[]
): boolean => {
  if (classGroups.length === 0) return true
  return tile.classGroup === null
    ? classGroups.includes(NO_CLASS_GROUP_FILTER_VALUE)
    : classGroups.includes(tile.classGroup)
}

const matchesAge = (
  tile: ArtworkTile,
  min: number | null,
  max: number | null
): boolean => {
  if (min === null && max === null) return true
  // An unknown age cannot satisfy a bound, so it drops out once any is set.
  if (tile.ageAtCreation === null) return false
  if (min !== null && tile.ageAtCreation < min) return false
  if (max !== null && tile.ageAtCreation > max) return false
  return true
}

export const applyArtworkTileFilters = (
  tiles: readonly ArtworkTile[],
  filters: ArtworkTileFilters
): ArtworkTile[] =>
  filter(
    tiles,
    (tile) =>
      matchesDateRange(tile, filters.dateRange) &&
      matchesLevel(tile, filters.levels) &&
      matchesClassGroup(tile, filters.classGroups) &&
      matchesAge(tile, filters.ageMin, filters.ageMax)
  )

export const countActiveArtworkTileFilters = (
  filters: ArtworkTileFilters
): number =>
  (filters.dateRange ? 1 : 0) +
  (filters.levels.length > 0 ? 1 : 0) +
  (filters.classGroups.length > 0 ? 1 : 0) +
  (filters.ageMin !== null || filters.ageMax !== null ? 1 : 0)

/**
 * One tile per portfolio project — the newest step wins. Pieces with no
 * `projectId` are never grouped, so a student's one-off work is untouched.
 *
 * Kept local rather than reusing the wall's `dedupeLatestVersions` so this tab
 * stays independent of that component; the grouping rule is the same one
 * (`projectId` identifies the project, newest `timeCreated` is the winner).
 */
export const dedupeLatestArtwork = (
  tiles: readonly ArtworkTile[]
): ArtworkTile[] => {
  const winners = new Map<string, ArtworkTile>()
  const ungrouped: ArtworkTile[] = []

  for (const tile of tiles) {
    const projectId = tile.artwork.projectId
    if (!projectId) {
      ungrouped.push(tile)
      continue
    }
    const existing = winners.get(projectId)
    if (
      !existing ||
      (tile.artwork.timeCreated ?? 0) > (existing.artwork.timeCreated ?? 0)
    ) {
      winners.set(projectId, tile)
    }
  }

  // Re-sorted rather than concatenated: the two groups interleave by date.
  return sortBy(
    [...values(Object.fromEntries(winners)), ...ungrouped],
    (tile) => -(tile.artwork.timeCreated ?? 0)
  )
}

/**
 * The hidden-visibility step of the tab's pipeline, applied after
 * `applyArtworkTileFilters` and before `groupTilesBySemester`.
 *
 * Hidden tiles never participate in `dedupeLatestArtwork` grouping — an
 * hidden newest step must not shadow the previous live step of the same
 * project. When `showHidden` is off they drop out entirely; when it is on
 * they interleave with the live tiles in their normal date position,
 * newest-first by `timeCreated` (the tab's usual order).
 */
export const selectVisibleArtworkTiles = (
  tiles: readonly ArtworkTile[],
  showHidden: boolean,
  latestOnly: boolean
): ArtworkTile[] => {
  const [hidden, live] = partition(tiles, (tile) => tile.isHidden)
  const visibleLive = latestOnly ? dedupeLatestArtwork(live) : live
  if (!showHidden) return [...visibleLive]
  return sortBy(
    [...visibleLive, ...hidden],
    (tile) => -(tile.artwork.timeCreated ?? 0)
  )
}

export const formatCoreLevel = (level: CoreSkillLevel): string =>
  `${ALL_SKILL_LEVEL_LABELS[level]} (${level})`

/** Level options for the filter: the core ladder, plus "No level". */
export const CORE_LEVEL_FILTER_OPTIONS: ReadonlyArray<{
  value: string
  label: string
}> = [
  ...map(Object.keys(ALL_SKILL_LEVELS[CLASS_GROUPS.CORE]), (level) => ({
    value: level,
    label: formatCoreLevel(level as CoreSkillLevel),
  })),
  { value: NO_LEVEL_FILTER_VALUE, label: "No level" },
]

/** Class-type options for the filter: the five groups, plus "Other". */
export const CLASS_GROUP_FILTER_OPTIONS: ReadonlyArray<{
  value: string
  label: string
}> = [
  ...map(values(CLASS_GROUPS), (group) => ({
    value: group,
    label: CLASS_GROUP_LABELS[group],
  })),
  { value: NO_CLASS_GROUP_FILTER_VALUE, label: "Other" },
]

const pluralizePieces = (count: number): string =>
  `${count} ${count === 1 ? "piece" : "pieces"}`

/**
 * `tiles` split by class name, ordered by each class's newest piece so the
 * most recent work leads. Tiles arrive newest-first and keep that order
 * within a bucket.
 */
const bucketTilesByClass = (
  tiles: readonly ArtworkTile[]
): ArtworkTileClassBucket[] => {
  const byClass = groupBy(tiles, (tile) => tile.className)
  const buckets = map(byClass, (bucketTiles, className) => ({
    key: className,
    label: className,
    tiles: bucketTiles,
  }))
  return sortBy(
    buckets,
    (bucket) => -(bucket.tiles[0].artwork.timeCreated ?? 0)
  )
}

/**
 * Semester sections, newest first. Semester keys sort lexically ("2026 B" >
 * "2026 A" > "2025 C"), and `tiles` arrives newest-first so each section keeps
 * that order internally. The subtitle carries the piece count and the core
 * level in force that semester; tiles are additionally bucketed by class for
 * labeled sub-groups.
 */
export const groupTilesBySemester = (
  tiles: readonly ArtworkTile[]
): ArtworkTileSection[] => {
  if (isEmpty(tiles)) return []
  const bySemester = groupBy(tiles, (tile) => tile.semester)
  const semesters = sortBy(Object.keys(bySemester)).reverse()
  return map(semesters, (semester) => {
    const sectionTiles = bySemester[semester]
    const level = sectionTiles[0].coreLevel
    const pieces = pluralizePieces(sectionTiles.length)
    return {
      key: semester,
      title: formatSemester(semester),
      subtitle: level === "" ? pieces : `${pieces} · ${formatCoreLevel(level)}`,
      tiles: sectionTiles,
      classBuckets: bucketTilesByClass(sectionTiles),
    }
  })
}

export const ARTWORK_ROTATION_STEP_DEGREES = 90

export const normalizeArtworkRotation = (degrees: number): number =>
  ((degrees % 360) + 360) % 360

/**
 * A Firestore update payload of `artwork.<id>.<field>` paths.
 *
 * Every writer on this tab builds one of these rather than a new `artwork`
 * map, because `updateDoc` replaces a named field wholesale: a map write drops
 * whatever another writer put there — a debounced rotation flush, an
 * add-to-portfolio flag, or an `ArtworkUploadModal` upload that landed
 * mid-write. Leaf paths merge server-side, so the writers need no ordering
 * between them.
 */
export type ArtworkFieldUpdate = Record<string, number | boolean | string>

/**
 * Field-path update setting absolute rotation targets (degrees by artwork id),
 * or null when none of the ids are present. Targets arrive batched because
 * rotate clicks save debounced — several pieces (or several turns of one
 * piece) land in a single write.
 *
 * An id missing from `artworkMap` is skipped rather than failing the batch:
 * writing its path would resurrect a deleted piece as a lone `rotation` with
 * no `url` or `classId`.
 *
 * `rotation` applies at DISPLAY time only — the stored pixels and the
 * `_thumb.jpg` / `_med.jpg` renditions are untouched, because the scanner and
 * customer apps read the angle themselves.
 */
export const buildArtworkRotationUpdate = (
  artworkMap: Record<string, StudentArtwork> | undefined,
  rotationTargets: Record<string, number>
): ArtworkFieldUpdate | null => {
  const update: ArtworkFieldUpdate = {}
  for (const [artworkId, target] of Object.entries(rotationTargets)) {
    if (!artworkMap?.[artworkId]) continue
    update[`artwork.${artworkId}.rotation`] = normalizeArtworkRotation(target)
  }
  return isEmpty(update) ? null : update
}

/**
 * Field-path update marking one piece as in the portfolio, or null when the id
 * is not present. Mirrors the old artwork section's add-to-portfolio write:
 * `inPortfolio: true` and `sortIndex` defaulted to 0 when absent. `projectId`
 * is absent from the payload, so an existing one survives.
 */
export const buildArtworkInPortfolioUpdate = (
  artworkMap: Record<string, StudentArtwork> | undefined,
  artworkId: string
): ArtworkFieldUpdate | null => {
  const current = artworkMap?.[artworkId]
  if (!current) return null
  return {
    [`artwork.${artworkId}.inPortfolio`]: true,
    [`artwork.${artworkId}.sortIndex`]: current.sortIndex ?? 0,
  }
}

/**
 * Field-path update hiding (or unhiding) one piece, or null when the id
 * is not present — writing paths for a deleted piece would resurrect it as a
 * skeleton entry. Hiding stamps when and by whom (`userId` must be the
 * audit `currentUserId`, never the effective user); restoring only flips the
 * flag, so the stamps remain as "last hidden" history.
 */
export const buildArtworkHiddenUpdate = (
  artworkMap: Record<string, StudentArtwork> | undefined,
  artworkId: string,
  isHidden: boolean,
  userId: string
): ArtworkFieldUpdate | null => {
  if (!artworkMap?.[artworkId]) return null
  if (!isHidden) return { [`artwork.${artworkId}.isHidden`]: false }
  return {
    [`artwork.${artworkId}.isHidden`]: true,
    [`artwork.${artworkId}.hiddenAt`]: Date.now(),
    [`artwork.${artworkId}.hiddenBy`]: userId,
  }
}

/**
 * Missing or false is not on the parent class-day card. Don't share wins
 * over a stored `isShared`, the same way it does for a message.
 */
export const isClassImageShared = (
  artwork: { isShared?: boolean; dontShare?: boolean } | null | undefined
): boolean => artwork?.isShared === true && artwork.dontShare !== true

export const isArtworkDontShare = (
  artwork: { dontShare?: boolean } | null | undefined
): boolean => artwork?.dontShare === true

/**
 * Field-path update turning Don't share on or off for one piece, or null
 * when the id is not present. On also unshares, so turning it back off
 * leaves the photo unshared until someone shares it. An empty reason
 * clears the stored one. Off only flips the flag. This never touches
 * `isHidden`: the piece stays in Jarvis either way.
 */
export const buildArtworkDontShareUpdate = (
  artworkMap: Record<string, StudentArtwork> | undefined,
  artworkId: string,
  dontShare: boolean,
  reason?: string
): ArtworkFieldUpdate | null => {
  if (!artworkMap?.[artworkId]) return null
  if (!dontShare) return { [`artwork.${artworkId}.dontShare`]: false }
  return {
    [`artwork.${artworkId}.dontShare`]: true,
    [`artwork.${artworkId}.isShared`]: false,
    [`artwork.${artworkId}.dontShareReason`]: reason?.trim() ?? "",
  }
}

/**
 * Field-path update sharing (or unsharing) one class scan, or null when the
 * piece is missing, hidden, or has no class session — those cannot sit on a
 * class-day card. `assignSessionId` fills that gap: Quality Check passes the
 * latest session of the class that already started, and the write stores it.
 * A session already on the piece is left alone. A don't-share piece cannot
 * be shared until that is turned off. Turning a photo shared stamps
 * `sharedAt` (the passed instant, or now) and `sharedBy` from `userId` (the
 * audit `currentUserId`, never the effective user). A photo that is already
 * shared keeps its existing stamps. Unshare writes only `isShared`.
 */
export const buildArtworkSharedUpdate = (
  artworkMap: Record<string, StudentArtwork> | undefined,
  artworkId: string,
  isShared: boolean,
  userId: string,
  sharedAt?: string,
  assignSessionId?: string
): ArtworkFieldUpdate | null => {
  const current = artworkMap?.[artworkId]
  if (!current) return null
  const classSessionId = current.classSessionId || assignSessionId
  if (!classSessionId) return null
  if (current.isHidden === true) return null
  if (isShared && current.dontShare === true) return null
  const sessionStamp = current.classSessionId
    ? {}
    : { [`artwork.${artworkId}.classSessionId`]: classSessionId }
  if (!isShared || current.isShared === true) {
    return {
      [`artwork.${artworkId}.isShared`]: isShared,
      ...sessionStamp,
    }
  }
  return {
    [`artwork.${artworkId}.isShared`]: true,
    [`artwork.${artworkId}.sharedAt`]: sharedAt ?? new Date().toISOString(),
    [`artwork.${artworkId}.sharedBy`]: userId,
    ...sessionStamp,
  }
}

export type ReviewClassImage = {
  studentId: string
  artwork: StudentArtwork
}

/** Who teaches a class. Scans are matched on this, not on `artwork.createdBy`. */
export type ReviewClassTeachers = {
  teacherIds?: readonly string[]
  defaultTeacherId?: string
  curriculumId?: string
}

const classHasTeacher = (
  classObj: ReviewClassTeachers | undefined,
  teacherId: string
): boolean =>
  (classObj?.teacherIds ?? []).includes(teacherId) ||
  classObj?.defaultTeacherId === teacherId

/**
 * Class scans to show on Standout Review next to notes and Story Time.
 * Hidden pieces, pieces with no class session, and pieces with no image URL
 * are omitted. Semester, class, and student search match the review filters.
 * A teacher filter keeps scans from that teacher's classes (`teacherIds` or
 * `defaultTeacherId`). `artwork.createdBy` is not the class teacher on
 * scanner and desktop uploads, so it is not used. A curriculum filter matches
 * the class, so a session of scans with no note still shows. A category
 * filter is about the note, so only scans from sessions that still have a
 * visible moment are included.
 */
export const selectClassImagesForReview = (
  students: readonly {
    id: string
    artwork?: Record<string, StudentArtwork>
  }[],
  options: {
    semester: string
    classId: string | null
    teacherId: string | null
    classesById: Record<string, ReviewClassTeachers | undefined>
    studentSearch: string
    namesByStudentId: Record<string, string | undefined>
    momentSessionIds: ReadonlySet<string>
    momentFiltersOpen: boolean
    curriculumId?: string | null
  }
): ReviewClassImage[] => {
  const search = options.studentSearch.trim().toLowerCase()
  const rows: ReviewClassImage[] = []
  for (const student of students) {
    const artwork = student.artwork
    if (!artwork) continue
    if (search) {
      const name = options.namesByStudentId[student.id] ?? student.id
      if (!name.toLowerCase().includes(search)) continue
    }
    for (const piece of Object.values(artwork)) {
      if (!piece?.id) continue
      if (piece.isHidden === true) continue
      if (!piece.classSessionId) continue
      if (!getArtworkDisplayUrl(piece)) continue
      if (getArtworkSemester(piece) !== options.semester) continue
      if (options.classId !== null && piece.classId !== options.classId) {
        continue
      }
      if (
        options.teacherId !== null &&
        !classHasTeacher(options.classesById[piece.classId], options.teacherId)
      ) {
        continue
      }
      if (
        options.curriculumId &&
        options.classesById[piece.classId ?? ""]?.curriculumId !==
          options.curriculumId
      ) {
        continue
      }
      if (
        !options.momentFiltersOpen &&
        !options.momentSessionIds.has(piece.classSessionId)
      ) {
        continue
      }
      rows.push({ studentId: student.id, artwork: piece })
    }
  }
  return rows
}
